import { Request, Response } from "express";
import mongoose from "mongoose";

import { Class, type IStudent, School, Student, User } from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class StudentController {
  /**
   * Get all students for the authenticated school with filtering and pagination
   * @route GET /api/v1/school/students
   * @access Private (School Admin)
   */
  async getAllSchoolStudents(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      // Prefer explicit school context if attached by middleware, fallback to user.schoolId
      let schoolId = (authReq as any).school?.id || authReq.user?.schoolId;
      const {
        status,
        approvalStatus,
        isApproved,
        academicLevel,
        academicYear,
        search,
        page = 1,
        limit = 10,
        sortBy = "createdAt",
        sortOrder = "desc",
      } = req.query;

      // If schoolId is missing, try to resolve it from the authenticated user (school users)
      if (!schoolId) {
        try {
          const userId = authReq.user?.userId;
          if (userId && mongoose.Types.ObjectId.isValid(userId)) {
            const foundSchool = await School.findOne({
              userId: new mongoose.Types.ObjectId(userId),
            }).select("_id schoolName isVerified");
            if (foundSchool) {
              schoolId = foundSchool._id.toString();
              // attach to request for downstream use
              (authReq as any).school = {
                id: schoolId,
                name: foundSchool.schoolName,
                isVerified: foundSchool.isVerified,
              };
            }
          }
        } catch (error) {
          logger.warn("Failed to auto-resolve schoolId from userId:", error);
        }

        if (!schoolId) {
          res.status(400).json({
            success: false,
            message: "School ID is required",
            error: "Invalid request",
          } as APIResponse);
          return;
        }
      }

      // Build query
      const queryFilter: any = {};
      // Safely set schoolId filter only when present
      if (schoolId) {
        // support comma-separated list or array
        const ids = Array.isArray(schoolId)
          ? schoolId
          : (schoolId as string)
              .split(",")
              .map(s => s.trim())
              .filter(Boolean);

        const objectIds: any[] = [];
        for (const id of ids) {
          if (mongoose.Types.ObjectId.isValid(id)) {
            objectIds.push(
              mongoose.Types.ObjectId.createFromHexString(id as string),
            );
          }
        }

        if (objectIds.length === 1) queryFilter.schoolId = objectIds[0];
        else if (objectIds.length > 1)
          queryFilter.schoolId = { $in: objectIds };
        else queryFilter.schoolId = schoolId; // fallback
      }

      if (status) queryFilter.status = status;
      if (academicLevel) queryFilter.academicLevel = academicLevel;
      if (academicYear) queryFilter.academicYear = academicYear;

      // Approval-status filter (pending | approved | rejected | cancelled).
      // Also accept comma-separated values like "approved,pending".
      if (approvalStatus) {
        const allowed = new Set([
          "pending",
          "approved",
          "rejected",
          "cancelled",
        ]);
        const values = (approvalStatus as string)
          .split(",")
          .map(v => v.trim().toLowerCase())
          .filter(v => allowed.has(v));
        if (values.length === 1) {
          queryFilter.approvalStatus = values[0];
        } else if (values.length > 1) {
          queryFilter.approvalStatus = { $in: values };
        }
      }

      // Backward-compat: ?isApproved=true|false maps to approvalStatus
      if (
        isApproved !== undefined &&
        queryFilter.approvalStatus === undefined
      ) {
        let flag = "";
        if (typeof isApproved === "string") {
          flag = isApproved.toLowerCase();
        } else if (typeof isApproved === "boolean") {
          flag = isApproved ? "true" : "false";
        }
        if (flag === "true") queryFilter.approvalStatus = "approved";
        else if (flag === "false")
          queryFilter.approvalStatus = { $ne: "approved" };
      }

      // Add search functionality
      if (search) {
        queryFilter.$or = [
          { fullName: { $regex: search as string, $options: "i" } },
          { studentId: { $regex: search as string, $options: "i" } },
          { email: { $regex: search as string, $options: "i" } },
        ];
      }

      // Build sort object
      const sort: any = {};
      sort[sortBy as string] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [students, totalCount] = await Promise.all([
        Student.find({ ...queryFilter })
          .sort(sort)
          .skip(skip)
          .limit(Number(limit))
          .populate("userId", "email isEmailVerified")
          .populate("schoolId", "schoolName"),
        Student.countDocuments({ ...queryFilter }),
      ]);

      // Debugging helper: when ?debug=true is passed, log and include the constructed query
      const debugMode =
        (req.query.debug as string | undefined)?.toLowerCase?.() === "true";
      if (debugMode) {
        logger.info("DEBUG getAllSchoolStudents - authReq.user:", authReq.user);
        logger.info(
          "DEBUG getAllSchoolStudents - authReq.school:",
          (authReq as any).school,
        );
        logger.info("DEBUG getAllSchoolStudents - queryFilter:", queryFilter);
      }

      // Format response data
      const formattedStudents = students.map(student => ({
        id: student._id,
        userId: (student.userId as any)?._id ?? student.userId,
        fullName: student.fullName,
        studentId: student.studentId,
        dateOfBirth: student.dateOfBirth,
        gender: student.gender,
        phone: student.phone,
        email: (student.userId as any)?.email,
        isEmailVerified: (student.userId as any)?.isEmailVerified,
        academicLevel: student.academicLevel,
        academicYear: student.academicYear,
        status: student.status,
        approvalStatus: (student as any).approvalStatus,
        isApproved: student.isApproved,
        rejectionReason: (student as any).rejectionReason,
        approvedAt: (student as any).approvedAt,
        profilePicture: student.profilePicture,
        school: {
          id: (student.schoolId as any)?._id,
          name: (student.schoolId as any)?.schoolName,
        },
        guardians: student.guardians,
        academicRecord: student.academicRecord,
        createdAt: student.createdAt,
        updatedAt: student.updatedAt,
      }));

      const totalPages = Math.ceil(totalCount / Number(limit));
      const hasNextPage = Number(page) < totalPages;
      const hasPrevPage = Number(page) > 1;

      const responseBody: any = {
        success: true,
        message: "Students retrieved successfully",
        data: {
          students: formattedStudents,
          pagination: {
            currentPage: Number(page),
            totalPages,
            totalCount,
            hasNextPage,
            hasPrevPage,
            limit: Number(limit),
          },
        },
      };

      if (debugMode) {
        responseBody.data.debug = {
          user: authReq.user || null,
          school: (authReq as any).school || null,
          queryFilter,
        };
      }

      res.status(200).json(responseBody as APIResponse);
    } catch (error) {
      logger.error("Get school students error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student by ID
   * @route GET /api/v1/school/students/:id
   * @access Private (School Admin)
   */
  async getStudentById(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      })
        .populate("userId", "email isEmailVerified")
        .populate("schoolId", "schoolName");

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Student retrieved successfully",
        data: {
          student: {
            id: student._id,
            userId: (student.userId as any)?._id ?? student.userId,
            fullName: student.fullName,
            studentId: student.studentId,
            dateOfBirth: student.dateOfBirth,
            gender: student.gender,
            phone: student.phone,
            email: (student.userId as any)?.email,
            isEmailVerified: (student.userId as any)?.isEmailVerified,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            status: student.status,
            approvalStatus: (student as any).approvalStatus,
            isApproved: student.isApproved,
            rejectionReason: (student as any).rejectionReason,
            approvedAt: (student as any).approvedAt,
            profilePicture: student.profilePicture,
            school: {
              id: (student.schoolId as any)?._id,
              name: (student.schoolId as any)?.schoolName,
            },
            guardians: student.guardians,
            academicRecord: student.academicRecord,
            preferences: student.preferences,
            medicalInfo: student.medicalInfo,
            documents: student.documents,
            createdAt: student.createdAt,
            updatedAt: student.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student by ID error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update student details
   * @route PUT /api/v1/school/students/:id
   * @access Private (School Admin)
   */
  async updateStudent(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const schoolId = authReq.user?.schoolId;
      const updateData = req.body;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Update student
      Object.assign(student, updateData);
      await student.save();

      logger.info(`Student updated: ${student.id}`, {
        studentId: student.studentId,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Student updated successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            status: student.status,
            isApproved: student.isApproved,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update student error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Approve student
   * @route PUT /api/v1/school/students/:id/approve
   * @access Private (School Admin)
   */
  async approveStudent(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Approve student
      const userId = authReq.user?.userId;
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User authentication required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }
      const approvedBy = mongoose.Types.ObjectId.createFromHexString(userId);
      await (student as any).approve(approvedBy);

      logger.info(`Student approved: ${student.id}`, {
        studentId: student.studentId,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Student approved successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            status: student.status,
            approvalStatus: (student as any).approvalStatus,
            isApproved: student.isApproved,
            approvedAt: (student as any).approvedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Approve student error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Reject student
   * @route PUT /api/v1/school/students/:id/reject
   * @access Private (School Admin)
   */
  async rejectStudent(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const { reason } = req.body;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!reason || reason.trim().length < 10) {
        res.status(400).json({
          success: false,
          message:
            "Rejection reason is required and must be at least 10 characters",
          error: "Invalid reason",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Reject student
      const userId = authReq.user?.userId;
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User authentication required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }
      const rejectedBy = mongoose.Types.ObjectId.createFromHexString(userId);
      await (student as any).reject(reason, rejectedBy);

      logger.info(`Student rejected: ${student.id}`, {
        studentId: student.studentId,
        schoolId,
        reason,
      });

      res.status(200).json({
        success: true,
        message: "Student rejected successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            status: student.status,
            approvalStatus: (student as any).approvalStatus,
            isApproved: student.isApproved,
            rejectionReason: (student as any).rejectionReason,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Reject student error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student classes
   * @route GET /api/v1/school/students/:id/classes
   * @access Private (School Admin)
   */
  async getStudentClasses(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const userId = authReq.user?.userId;
      const userRole = authReq.user?.role;
      let schoolId = authReq.user?.schoolId;
      let studentObjectId: mongoose.Types.ObjectId | null = null;
      const { status, semesterId, page = 1, limit = 10 } = req.query;

      // Resolve student and school context
      let student: IStudent | null = null;
      if (userRole === "student") {
        // For student tokens, find by userId and derive schoolId and studentId
        if (!userId || !mongoose.Types.ObjectId.isValid(userId)) {
          res.status(401).json({
            success: false,
            message: "User not authenticated",
            error: "Invalid request",
          } as APIResponse);
          return;
        }

        student = await Student.findOne({
          userId: new mongoose.Types.ObjectId(userId),
        });

        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student profile not found",
            error: "Student not found",
          } as APIResponse);
          return;
        }

        schoolId = student.schoolId ? student.schoolId.toString() : schoolId;
        studentObjectId = student._id;
      } else {
        // School/admin path expects studentId param and schoolId in token
        if (!schoolId) {
          res.status(400).json({
            success: false,
            message: "School ID is required",
            error: "Invalid request",
          } as APIResponse);
          return;
        }

        if (!mongoose.Types.ObjectId.isValid(id)) {
          res.status(400).json({
            success: false,
            message: "Invalid student ID",
            error: "Invalid ID format",
          } as APIResponse);
          return;
        }

        studentObjectId = new mongoose.Types.ObjectId(id);
        student = await Student.findOne({
          _id: studentObjectId,
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });

        if (!student) {
          res.status(404).json({
            success: false,
            message: "Student not found",
            error: "Student not found",
          } as APIResponse);
          return;
        }
      }

      // Build class query
      const classFilter: any = {
        studentIds: studentObjectId,
        ...(schoolId
          ? { schoolId: new mongoose.Types.ObjectId(schoolId) }
          : {}),
      };

      if (status) classFilter.status = status;
      if (semesterId)
        classFilter.semesterId = new mongoose.Types.ObjectId(
          semesterId as string,
        );

      // Calculate pagination
      const skip = (Number(page) - 1) * Number(limit);

      const [classes, totalCount] = await Promise.all([
        Class.find({ ...classFilter })
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(Number(limit))
          .populate("teacherIds", "fullName email")
          .populate("semesterId", "semesterName academicYear"),
        Class.countDocuments({ ...classFilter }),
      ]);

      // Format classes
      const formattedClasses = classes.map(classItem => ({
        id: classItem._id,
        className: classItem.className,
        classCode: classItem.classCode,
        subject: classItem.subject,
        description: classItem.description,
        status: classItem.status,
        isActive: classItem.isActive,
        maxStudents: classItem.maxStudents,
        currentStudents: classItem.studentIds?.length || 0,
        schedule: classItem.schedule,
        teacher:
          classItem.teacherIds && classItem.teacherIds.length > 0
            ? {
                id: (classItem.teacherIds[0] as any)._id,
                fullName: (classItem.teacherIds[0] as any).fullName,
                email: (classItem.teacherIds[0] as any).email,
              }
            : null,
        semester: classItem.semesterId
          ? {
              id: (classItem.semesterId as any)._id,
              name: (classItem.semesterId as any).semesterName,
              academicYear: (classItem.semesterId as any).academicYear,
            }
          : null,
        createdAt: classItem.createdAt,
        updatedAt: classItem.updatedAt,
      }));

      const totalPages = Math.ceil(totalCount / Number(limit));
      const hasNextPage = Number(page) < totalPages;
      const hasPrevPage = Number(page) > 1;

      res.status(200).json({
        success: true,
        message: "Student classes retrieved successfully",
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
          },
          classes: formattedClasses,
          pagination: {
            currentPage: Number(page),
            totalPages,
            totalCount,
            hasNextPage,
            hasPrevPage,
            limit: Number(limit),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student classes error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get student statistics
   * @route GET /api/v1/school/students/statistics
   * @access Private (School Admin)
   */
  async getStudentStatistics(req: Request, res: Response): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Get student statistics
      const [
        totalStudents,
        approvedStudents,
        pendingStudents,
        rejectedStudents,
        studentsByAcademicLevel,
        studentsByStatus,
      ] = await Promise.all([
        Student.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
        }),
        Student.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          isApproved: true,
        }),
        Student.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          status: "pending",
        }),
        Student.countDocuments({
          schoolId: new mongoose.Types.ObjectId(schoolId),
          status: "rejected",
        }),
        Student.aggregate([
          { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
          { $group: { _id: "$academicLevel", count: { $sum: 1 } } },
          { $sort: { _id: 1 } },
        ]),
        Student.aggregate([
          { $match: { schoolId: new mongoose.Types.ObjectId(schoolId) } },
          { $group: { _id: "$status", count: { $sum: 1 } } },
        ]),
      ]);

      res.status(200).json({
        success: true,
        message: "Student statistics retrieved successfully",
        data: {
          overview: {
            totalStudents,
            approvedStudents,
            pendingStudents,
            rejectedStudents,
            approvalRate:
              totalStudents > 0
                ? Math.round((approvedStudents / totalStudents) * 100)
                : 0,
          },
          byAcademicLevel: studentsByAcademicLevel.map(item => ({
            academicLevel: item._id,
            count: item.count,
          })),
          byStatus: studentsByStatus.map(item => ({
            status: item._id,
            count: item.count,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student statistics error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Update student approval status (PATCH)
   * @route PATCH /api/v1/school/students/:id
   * @access Private (School Admin)
   * @body { isApproved: boolean, reason?: string }
   */
  async updateStudentApprovalStatus(
    req: Request,
    res: Response,
  ): Promise<void> {
    try {
      const authReq = req as AuthRequest;
      const { id } = req.params;
      const { isApproved, reason } = req.body;
      const schoolId = authReq.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (typeof isApproved !== "boolean") {
        res.status(400).json({
          success: false,
          message: "isApproved must be a boolean",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // If rejecting, reason is required
      if (!isApproved && (!reason || reason.trim().length < 5)) {
        res.status(400).json({
          success: false,
          message:
            "Rejection reason is required and must be at least 5 characters",
          error: "Invalid reason",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.isValid(schoolId)
          ? mongoose.Types.ObjectId.createFromHexString(schoolId)
          : schoolId,
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const userId = authReq.user?.userId;
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User authentication required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Update approval status
      await (isApproved
        ? (student as any).approve(
            mongoose.Types.ObjectId.createFromHexString(userId),
          )
        : (student as any).reject(
            reason,
            mongoose.Types.ObjectId.createFromHexString(userId),
          ));

      logger.info(`Student approval status updated: ${student.id}`, {
        studentId: student.studentId,
        schoolId,
        isApproved,
      });

      res.status(200).json({
        success: true,
        message: `Student ${isApproved ? "approved" : "rejected"} successfully`,
        data: {
          student: {
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            status: student.status,
            isApproved: student.isApproved,
            approvalStatus: student.approvalStatus,
            rejectionReason: student.rejectionReason,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Update student approval status error:", error);
      res.status(500).json({
        success: false,
        message: "Internal server error. Please try again later.",
      } as APIResponse);
    }
  }

  /**
   * Get a single class by ID for the authenticated student (must be enrolled)
   * @route GET /api/v1/student/classes/:id
   * @access Private (Student only)
   */
  async getStudentClassById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id: classId } = req.params;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(classId)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).lean();
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findById(classId)
        .populate("schoolId", "schoolName address")
        .populate("semesterId", "semesterName academicYear startDate endDate")
        .populate("teacherIds", "fullName employeeId profilePicture")
        .lean();

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const studentObjectId = student._id;
      const isEnrolled = classItem.studentIds?.some(
        (sid: mongoose.Types.ObjectId) =>
          sid.toString() === studentObjectId.toString(),
      );
      if (!isEnrolled) {
        res.status(403).json({
          success: false,
          message: "You are not enrolled in this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const scheduleResponse =
        classItem.schedule && Array.isArray(classItem.schedule)
          ? classItem.schedule.map((scheduleItem: any) => ({
              day: scheduleItem.day,
              startTime: scheduleItem.startTime,
              endTime: scheduleItem.endTime,
              isActive: scheduleItem.isActive ?? false,
            }))
          : [];

      const finalAttendanceTime =
        (classItem as any)?.settings?.finalAttendanceTime || null;

      res.status(200).json({
        success: true,
        message: "Class retrieved successfully",
        data: {
          class: {
            id: classItem._id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            credits: classItem.credits,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.studentIds?.length || 0,
            availableSpots:
              classItem.maxStudents &&
              classItem.maxStudents > (classItem.studentIds?.length || 0)
                ? classItem.maxStudents - (classItem.studentIds?.length || 0)
                : 0,
            classRoom: classItem.classRoom,
            classRoomNumber: classItem.classRoomNumber,
            restRoomMinutes: classItem.restRoomMinutes,
            location: {
              lat:
                (classItem as any).location?.latitude == null
                  ? "0.0"
                  : String((classItem as any).location.latitude),
              lng:
                (classItem as any).location?.longitude == null
                  ? "0.0"
                  : String((classItem as any).location.longitude),
            },
            schedule: scheduleResponse,
            finalAttendance: finalAttendanceTime,
            school: classItem.schoolId
              ? {
                  id: (classItem.schoolId as any)._id,
                  name: (classItem.schoolId as any).schoolName,
                  address: (classItem.schoolId as any).address,
                }
              : null,
            semester: classItem.semesterId
              ? {
                  id: (classItem.semesterId as any)._id,
                  name: (classItem.semesterId as any).semesterName,
                  academicYear: (classItem.semesterId as any).academicYear,
                  startDate: (classItem.semesterId as any).startDate,
                  endDate: (classItem.semesterId as any).endDate,
                }
              : null,
            teachers:
              (classItem.teacherIds as any[])?.map((t: any) => ({
                id: t._id,
                fullName: t.fullName,
                employeeId: t.employeeId,
                profilePicture: t.profilePicture,
              })) || [],
            enrolledStudentsCount: classItem.studentIds?.length || 0,
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching student class by ID:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch class",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

export const studentController = new StudentController();
